import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Loader2, Home, ArrowRight, PartyPopper, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import Layout from '../components/Layout';
import { subscriptionsApi } from '../services/api';
import { toast } from 'sonner';

const SubscriptionSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  
  const sessionId = searchParams.get('session_id');
  
  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);
  
  useEffect(() => {
    // Poll for status if still checking
    if (status === 'checking' && pollCount < 5) {
      const timer = setTimeout(() => {
        checkPaymentStatus();
        setPollCount(prev => prev + 1);
      }, 2000);
      return () => clearTimeout(timer);
    } else if (status === 'checking' && pollCount >= 5) {
      setStatus('timeout');
    }
  }, [status, pollCount]);
  
  const checkPaymentStatus = async () => {
    try {
      const res = await subscriptionsApi.getCheckoutStatus(sessionId);
      
      if (res.data.payment_status === 'paid' && res.data.subscription_activated) {
        setStatus('success');
        setSubscriptionData(res.data);
        toast.success('Subscription activated successfully!');
      } else if (res.data.status === 'expired') {
        setStatus('expired');
      } else {
        // Keep checking
        setStatus('checking');
      }
    } catch (error) {
      console.error('Status check error:', error);
      if (pollCount >= 4) {
        setStatus('error');
      }
    }
  };
  
  const getTierDisplay = (tier) => {
    const displays = {
      standard: { name: 'Standard Listing', color: 'bg-slate-500' },
      featured: { name: 'Featured Priority', color: 'bg-amber-500' },
      concierge: { name: 'Concierge Onboarding', color: 'bg-trust-blue' }
    };
    return displays[tier] || displays.standard;
  };
  
  if (status === 'checking') {
    return (
      <Layout>
        <div className="min-h-[70vh] flex flex-col items-center justify-center px-4">
          <Loader2 className="w-12 h-12 animate-spin text-trust-blue mb-6" />
          <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">
            Activating Your Subscription
          </h1>
          <p className="text-slate-600">
            Please wait while we confirm your payment...
          </p>
        </div>
      </Layout>
    );
  }
  
  if (status === 'success') {
    const tierDisplay = getTierDisplay(subscriptionData?.tier);
    
    return (
      <Layout>
        <div className="min-h-[70vh] flex items-center justify-center px-4 py-16">
          <div className="max-w-lg w-full text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
              <PartyPopper className="w-10 h-10 text-green-600" />
            </div>
            
            <h1 className="font-heading text-3xl font-bold text-slate-900 mb-4">
              Welcome to Stay Pair Homes!
            </h1>
            
            <div className={`inline-flex items-center gap-2 ${tierDisplay.color} text-white px-4 py-2 rounded-full mb-6`}>
              <Star className="w-4 h-4" />
              <span className="font-medium">{tierDisplay.name}</span>
            </div>
            
            <p className="text-lg text-slate-600 mb-8">
              Your subscription is now active. You're ready to create your listing
              and start connecting with renters!
            </p>
            
            <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-8">
              <h3 className="font-semibold text-green-800 mb-3 flex items-center justify-center gap-2">
                <Check className="w-5 h-5" />
                What's Included
              </h3>
              <ul className="text-left text-green-700 space-y-2">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  Publish unlimited listings
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  Accept unlimited bookings
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  Direct messaging with renters
                </li>
                {subscriptionData?.tier !== 'standard' && (
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    Featured badge on listings
                  </li>
                )}
                {subscriptionData?.tier === 'concierge' && (
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    Priority concierge support
                  </li>
                )}
              </ul>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-trust-blue hover:bg-trust-blue-hover text-white"
                onClick={() => navigate('/create-listing')}
                data-testid="create-listing-btn"
              >
                <Home className="w-5 h-5 mr-2" />
                Create Your First Listing
              </Button>
              
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate('/dashboard')}
                data-testid="go-dashboard-btn"
              >
                Go to Dashboard
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }
  
  // Error or timeout state
  return (
    <Layout>
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-16">
        <div className="max-w-lg w-full text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-amber-100 rounded-full mb-6">
            <Loader2 className="w-10 h-10 text-amber-600" />
          </div>
          
          <h1 className="font-heading text-3xl font-bold text-slate-900 mb-4">
            {status === 'expired' ? 'Session Expired' : 'Payment Processing'}
          </h1>
          
          <p className="text-lg text-slate-600 mb-8">
            {status === 'expired' 
              ? 'Your checkout session has expired. Please try again.'
              : 'We\'re still confirming your payment. This may take a moment. You can check your dashboard for the latest status.'
            }
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-trust-blue hover:bg-trust-blue-hover text-white"
              onClick={() => navigate('/dashboard')}
            >
              Go to Dashboard
            </Button>
            
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/pricing')}
            >
              View Plans
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SubscriptionSuccessPage;
